import React, { useCallback, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { api } from '../../lib/api';
import { useCurrentTenant } from '../../lib/TenantBrandingContext';
import { tenantUrl } from '../../lib/publicDomain';

const qrUrl = (code, tenantSlug) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=180x180&ecc=M&data=${encodeURIComponent(
    `${tenantUrl(tenantSlug)}/?join=${code}`
  )}`;

const CohortInviteLinkPanel = ({ cohort, token }) => {
  const { tenant } = useCurrentTenant();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [copied, setCopied] = useState('');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listCohortInviteLinks(cohort.id, token);
      setLinks(Array.isArray(result?.inviteLinks) ? result.inviteLinks : []);
      setError('');
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to load invite links.');
    } finally {
      setLoading(false);
    }
  }, [cohort.id, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const inviteUrl = (code) =>
    `${tenantUrl(tenant?.slug || 'your-institution')}/?join=${code}`;

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await api.createCohortInviteLink(cohort.id, { label: newLabel }, token);
      setNewLabel('');
      await refresh();
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to create link.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (link) => {
    try {
      await api.updateCohortInviteLink(link.id, { isActive: !link.isActive }, token);
      await refresh();
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to update link.');
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm('Delete this invite link? Students who have already joined will not be affected.')) return;
    try {
      await api.deleteCohortInviteLink(link.id, token);
      await refresh();
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to delete link.');
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(inviteUrl(code)).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  return (
    <div className="mt-3 p-3 border rounded bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Registration links — {cohort.name}</h6>
      </div>
      <p className="small text-muted mb-3">
        Share a link with students so they self-register and auto-join this cohort. Each link has a unique 8-character code.
      </p>

      {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

      {loading ? (
        <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
      ) : (
        <>
          {links.length === 0 ? (
            <p className="text-muted small mb-3">No links yet.</p>
          ) : (
            <div className="d-flex flex-column gap-3 mb-3">
              {links.map((link) => (
                <div key={link.id} className={`border rounded p-3 bg-white${!link.isActive ? ' opacity-50' : ''}`}>
                  <div className="d-flex gap-3 align-items-start">
                    {tenant?.slug ? (
                      <img
                        src={qrUrl(link.code, tenant.slug)}
                        alt={`QR code for ${link.code}`}
                        width={90}
                        height={90}
                        className="flex-shrink-0"
                      />
                    ) : null}
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <code className="fs-6 fw-bold">{link.code}</code>
                        {!link.isActive && <Badge bg="secondary">Inactive</Badge>}
                        <Badge bg="info">{link.joinCount} joined</Badge>
                      </div>
                      {link.label ? <p className="small text-muted mb-1">{link.label}</p> : null}
                      <p className="small text-break mb-2" style={{ wordBreak: 'break-all' }}>
                        {inviteUrl(link.code)}
                      </p>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant={copied === link.code ? 'success' : 'outline-primary'}
                          onClick={() => handleCopy(link.code)}
                        >
                          {copied === link.code ? 'Copied!' : 'Copy link'}
                        </Button>
                        <Button
                          size="sm"
                          variant={link.isActive ? 'outline-warning' : 'outline-success'}
                          onClick={() => handleToggleActive(link)}
                        >
                          {link.isActive ? 'Deactivate' : 'Reactivate'}
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(link)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="d-flex gap-2 align-items-end">
            <Form.Group className="flex-grow-1">
              <Form.Label className="small mb-1">Label <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control
                size="sm"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Sent to VLE October 2025"
              />
            </Form.Group>
            <Button size="sm" variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? 'Generating…' : 'Generate link'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CohortInviteLinkPanel;
